import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { Observable, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ApiService } from '@services/api.service';

export interface ZoomConfig {
  client_id: string;
  redirect_uri: string;
  authorization_uri: string;
}

export interface MicrosoftDedicatedSenderConfig {
  client_id: string;
  tenant_id: string;
  redirect_uri: string;
  authorization_uri: string;
  service_user_email: string;
}

export interface ApiConfigOptionsResponse {
  zoom: ZoomConfig;
  microsoft_dedicated_sender: MicrosoftDedicatedSenderConfig;
}

export interface ZoomTrainerValidationResponse {
  trainers_valid: boolean;
  trainers: Array<{ id: string; zoom_id: string | null }>;
}

@Injectable({
  providedIn: 'root',
})
export class IntegrationsService {
  constructor(private api: ApiService) {}

  syncPersonio(id: string): Observable<void> {
    const subject = new Subject<void>();
    this.api
      .post(environment.backendBaseUrl, '/api/v1/members/personio_import', {
        api_config_id: id,
      })
      .pipe(take(1))
      .subscribe(
        () => {
          subject.next();
        },
        error => {
          subject.error(error);
        },
      );
    return subject.asObservable();
  }

  customAttributes(id: string): Observable<any> {
    return this.api
      .post(environment.backendBaseUrl, '/api/v1/members/custom_attributes', {
        api_config_id: id,
      })
      .pipe(
        take(1),
        map(response => response.body),
      );
  }

  getZoomApiConfigOptions(): Observable<ZoomConfig> {
    return this.api
      .get<ApiConfigOptionsResponse>(
        environment.backendBaseUrl,
        '/api/v1/api_configs/options',
      )
      .pipe(
        take(1),
        map(response => response.body.zoom),
      );
  }

  exchangeZoomAuthCode(code: string): Observable<void> {
    return this.api.post(environment.backendBaseUrl, `/api/v1/zoom/authorize`, {
      auth_code: code,
    });
  }

  validateZoomTrainers(
    trainerIds: string[],
  ): Observable<ZoomTrainerValidationResponse> {
    return this.api
      .post(environment.backendBaseUrl, '/api/v1/zoom/validate_trainers', {
        trainer_ids: trainerIds,
      })
      .pipe(
        take(1),
        map(response => response.body),
      );
  }

  getMicrosoftDedicatedSenderApiConfigOptions(): Observable<MicrosoftDedicatedSenderConfig> {
    return this.api
      .get<ApiConfigOptionsResponse>(
        environment.backendBaseUrl,
        '/api/v1/api_configs/options',
      )
      .pipe(
        take(1),
        map(response => response.body.microsoft_dedicated_sender),
      );
  }

  exchangeMicrosoftAuthCode(code: string): Observable<void> {
    return this.api.post(
      environment.backendBaseUrl,
      '/api/v1/microsoft_oauth/authorize',
      {
        auth_code: code,
      },
    );
  }
}
